import FJS3NetworkManager from './FJS3NetworkManager';
import FJS3Config from '../config/FJS3Config';
import FJUtil from '../util/FJUtil';
import FJVideoUtil from '@src/util/FJVideoUtil';
import FJNetworkManager from '../core/FJNetworkManager';
import FJRequirementsUtil from '@src/util/FJRequirementsUtil';
import { GENERATE_STATE, JOB_ERROR_CODE, JOB_STATE, PROCESS_TYPE } from '@src/constants/FJEnumConstant';
import FJCoreConfig from '@src/core/FJCoreConfig';
import FJSubtitleUtil from '../util/FJSubtitleUtil';
import FJLongToShortUpdateStore from './FJLongToShortUpdateStore';
import FJConfig from '../config/FJConfig';

const MAX_POLLING_ERROR_TIME = 3;
const TASK_POLLING_INTERVAL = 5 * 1000;

/** 接口无返回时，用占位数据通知监听方任务失败 */
function buildPollingFailurePayloads(eventIds) {
    return eventIds.map(id => ({
        event_id: id,
        job_state: JOB_STATE.FAILED,
        out_put: { job_state: JOB_STATE.FAILED },
    }));
}

function normalizePollingResult(result) {
    return Array.isArray(result) ? result : [result];
}

/** 轮询项进入终态：需从队列移除并刷新积分 */
const POLLING_TERMINAL_JOB_STATES = new Set([
    JOB_STATE.SUCCEEDED,
    JOB_STATE.TIMEOUT,
    JOB_STATE.FAILED,
    JOB_STATE.CANCELED,
    JOB_STATE.DELETED,
]);

/** 长转短任务进度条各阶段默认权重（0~1，整条为 1）。修改此处即可统一调比例。 */
const DEFAULT_PROCESS_PROGRESS_WEIGHTS = {
    /** 上传阶段总占比；仅视频时独占；需同时上传音频时与音频各分一半 */
    uploadTotal: 0.1,
    backend: 0.65,
    backendWhenNoFaceAndExport: 0.85, // 后端阶段中，无脸数据时占比
    /** 人脸检测：不需要导出时 / 需要导出时 */
    faceWhenNoExport: 0.2,
    faceWhenExport: 0.125,
    /** 导出：无脸数据时 / 有脸数据时 */
    exportWhenNoFaceData: 0.2,
    exportWhenFaceData: 0.1,
    cover: 0.05,
};

function mergeProcessProgressWeights(overrides) {
    return { ...DEFAULT_PROCESS_PROGRESS_WEIGHTS, ...overrides };
}

/**
 * 按顺序累加各段 [上传视频 → 上传音频? → 后端 → 人脸? → 导出? → 封面]，得到每段 start / proportion。
 * @param {{ disableFaceData?: boolean, needExport?: boolean, needUploadAudio?: boolean }} options
 * @param {typeof DEFAULT_PROCESS_PROGRESS_WEIGHTS} weights
 */
function computeProcessProgressSegments(options, weights) {
    const {
        disableFaceData = false,
        needExport = FJCoreConfig.needExport,
        needUploadAudio = FJCoreConfig.needUploadAudio,
    } = options;
    const w = weights;

    const uploadVideoS = 0;
    const uploadVideoP = needUploadAudio ? w.uploadTotal / 2 : w.uploadTotal;
    const uploadAudioS = uploadVideoS + uploadVideoP;
    const uploadAudioP = needUploadAudio ? w.uploadTotal / 2 : 0;

    const backendS = uploadAudioS + uploadAudioP;
    const backendP = disableFaceData && !needExport ? w.backendWhenNoFaceAndExport : w.backend;

    const facetS = backendS + backendP;
    const faceP = disableFaceData ? 0 : !needExport ? w.faceWhenNoExport : w.faceWhenExport;

    const exportS = facetS + faceP;
    const exportP = !needExport ? 0 : disableFaceData ? w.exportWhenNoFaceData : w.exportWhenFaceData;

    const coverThumbnailsS = exportS + exportP;
    const coverThumbnailsP = w.cover;

    return {
        uploadVideoS,
        uploadVideoP,
        uploadAudioS,
        uploadAudioP,
        backendS,
        backendP,
        facetS,
        faceP,
        exportS,
        exportP,
        coverThumbnailsS,
        coverThumbnailsP,
    };
}

const FJLongToShortStore = {
    _fileData: null,
    _currentTaskIds: [],
    _errorTime: 0,
    _isPolling: false,
    _timer: null,
    /**
     * 轮询任务监听：每项为 { progress, polling }，成对注册/成对移除，避免两条平行数组索引错位。
     * progress 可为 null（仅关心轮询结果的任务，如 startSubTask）。
     */
    _taskWatchSubscriptions: [],
    _curFailedTaskId: null,
    _curFailedErrorCode: null,
    getCurFailedTaskId() {
        return this._curFailedTaskId;
    },
    getCurFailedErrorCode() {
        return this._curFailedErrorCode;
    },
    /**
     * @param {string | null} id
     * @param {string | null | undefined} [errorCode]
     */
    setCurFailedTaskId(id, errorCode = null) {
        this._curFailedTaskId = id;
        this._curFailedErrorCode = id ? (errorCode ?? null) : null;
    },
    setFileData({ duration, name, thumbnailUrl, thumbnailBlob, file, width, height, fileDownLoadUrl, isExistS3 }) {
        const prev = this._fileData;
        if (prev?.thumbnailUrl && prev.thumbnailUrl !== thumbnailUrl) {
            if (typeof prev.thumbnailUrl === 'string' && prev.thumbnailUrl.startsWith('blob:')) {
                try {
                    URL.revokeObjectURL(prev.thumbnailUrl);
                } catch {
                    /* noop */
                }
            }
        }
        this._fileData = {
            duration,
            name,
            thumbnailUrl,
            thumbnailBlob,
            file,
            width,
            height,
            fileDownLoadUrl,
            isExistS3,
        };
    },

    getFileData() {
        return this._fileData;
    },
    getFileDuration() {
        return this._fileData?.duration;
    },
    beforeCheck(totalCredits, freeTimes, duration) {
        const isLogin = FJRequirementsUtil.isLogin();
        if (!isLogin) {
            FJRequirementsUtil.login();
            return false;
        }
        if (!duration) {
            return false;
        }
        const useCreditAmount = this.getFileUseCreditAmount(duration);
        if (totalCredits < useCreditAmount && freeTimes <= 0) {
            FJRequirementsUtil.getCredits();
            return false;
        }
        return true;
    },
    getFileUseCreditAmount(duration) {
        if (!duration) {
            return 0;
        }
        return Math.floor(duration / 60);
    },
    clearFileData() {
        const thumb = this._fileData?.thumbnailUrl;
        if (typeof thumb === 'string' && thumb.startsWith('blob:')) {
            try {
                URL.revokeObjectURL(thumb);
            } catch {
                /* noop */
            }
        }
        this._fileData = null;
    },
    _addPollingTasks(eventIds) {
        eventIds.forEach(eventId => {
            this._currentTaskIds.indexOf(eventId) === -1 && this._currentTaskIds.push(eventId);
        });
    },
    _deletePollingTask(eventId) {
        this._currentTaskIds = this._currentTaskIds.filter(id => id !== eventId);
    },
    getPollingTaskIds() {
        return this._currentTaskIds || [];
    },
    /**
     * @param {((payload: { id: unknown, progress: number }) => void) | null} onProgress
     * @param {(dataList: unknown[]) => void} onPolling
     * @returns {() => void} 取消本组监听（进度 + 轮询一并移除）
     */
    _registerTaskWatchSubscription(onProgress, onPolling) {
        const entry = { progress: onProgress ?? null, polling: onPolling };
        this._taskWatchSubscriptions.push(entry);
        return () => this._disposeTaskWatchSubscription(entry);
    },

    _disposeTaskWatchSubscription(entry) {
        this._taskWatchSubscriptions = this._taskWatchSubscriptions.filter(e => e !== entry);
        if (!this._taskWatchSubscriptions.length) {
            this._clearPolling();
        }
    },
    async start(options = {}) {
        this._errorTime = 0;
        this._timer = null;
        const { callback, progressCallback } = options;
        try {
            const params = await this._getParams({ ...options, callback: progressCallback });

            const eventId = await this._createGeneratingTask(params);
            FJRequirementsUtil.updateAllCreditInfo();

            if (!eventId) {
                FJLongToShortUpdateStore._notifyNetworkError();
                callback?.({ status: GENERATE_STATE.FAILED, errorCode: JOB_ERROR_CODE.UPLOAD_ERROR });
                return;
            }
            this._addPollingTasks([eventId]);

            const _progressCallback = ({ id, progress }) => {
                if (id === eventId) {
                    progressCallback?.({ id, progress });
                }
            };
            let unsubscribe;
            const _pollingCallback = dataList => {
                const data = dataList.find(data => data.event_id === eventId);
                if (data) {
                    const list = this.parseTaskList([data]);
                    callback?.(list[0]);

                    if (list[0].status !== GENERATE_STATE.PROCESSING) {
                        unsubscribe();
                    }
                }
            };
            unsubscribe = this._registerTaskWatchSubscription(_progressCallback, _pollingCallback);
            !this._isPolling && this._pollingTask();

            return eventId;
        } catch {
            callback?.({ status: GENERATE_STATE.FAILED, errorCode: JOB_ERROR_CODE.UPLOAD_ERROR });

            return;
        }
    },

    async _getParams(options = {}) {
        const {
            textLanguageKey,
            genreKey,
            clipLengthKey,
            aspectRatioKey,
            subtitleStyleId,
            captureKeyMoments,
            videoMeta,
            callback,
        } = options;
        const {
            duration,
            name,
            width,
            height,
            file: videoFile,
            fileDownLoadUrl,
            isExistS3,
            thumbnailBlob,
        } = this.getFileData() ?? {};
        const { youtubeKey = '', sourceUrl = '', resolutionLabel = '' } = videoMeta ?? {};
        const disableFaceData =
            FJUtil.aspectRatioKeyMatchesFileData(aspectRatioKey, width, height) ||
            FJUtil.aspectRatioKeyIs34vs45CrossFromFile(aspectRatioKey, width, height);

        // 上传视频 + 缩略图并行：二者无数据依赖，墙钟时间约 max(上传, 截帧+上传) 而非相加
        const key = isExistS3
            ? FJUtil.getBaseName(fileDownLoadUrl)
            : youtubeKey
              ? youtubeKey
              : await FJUtil.hashEncode(videoFile);
        const videoUploadKey = FJS3Config.INPUT_UPLOAD_KEY + key + '.mp4';
        const thumbnailUploadKey = FJS3Config.INPUT_UPLOAD_KEY + key + '.jpg';
        const fileOrUrl = fileDownLoadUrl || videoFile;
        const uploadVideoTask = (async () => {
            const base = FJLongToShortStore.getProcessBase(PROCESS_TYPE.UPLOAD_VIDEO, {
                needUploadAudio: FJCoreConfig.needUploadAudio,
            });
            if (isExistS3) {
                callback?.({ id: '', progress: base.proportion + base.start });
                return true;
            }
            return FJS3NetworkManager.uploadFileAsync(videoFile, videoUploadKey, {
                progress: p => {
                    const _p = p * base.proportion + base.start;
                    callback?.({ id: '', progress: _p });
                },
            });
        })();
        const uploadThumbnailTask = (async () => {
            return FJS3NetworkManager.uploadFileAsync(thumbnailBlob, thumbnailUploadKey);
        })();

        const [videoOk, thumbOk] = await Promise.all([uploadVideoTask, uploadThumbnailTask]);
        if (!videoOk || !thumbOk) {
            callback?.({ status: GENERATE_STATE.FAILED, errorCode: JOB_ERROR_CODE.UPLOAD_ERROR });
            return;
        }
        // 上传音频
        let audioUrl = null;
        if (FJCoreConfig.needUploadAudio) {
            const audioFormat = 'mp3';
            const base = FJLongToShortStore.getProcessBase(PROCESS_TYPE.UPLOAD_AUDIO, {
                needUploadAudio: FJCoreConfig.needUploadAudio,
            });
            const audioFile = await FJVideoUtil.getAudioFromVideo(videoFile, {
                format: audioFormat,
                decodeCallback: () => {
                    callback?.({ id: '', progress: base.start + base.proportion * 0.5 });
                },
            });
            const audioUploadKey = FJS3Config.INPUT_UPLOAD_KEY + key + '.' + audioFormat;
            await FJS3NetworkManager.uploadFileAsync(audioFile, audioUploadKey, {
                progress: p => {
                    const _p = base.start + base.proportion * 0.5 + p * (base.proportion * 0.5);
                    callback?.({ id: '', progress: _p });
                },
            });
            audioUrl = FJS3Config.S3_SERVER_HOST + audioUploadKey;
        }
        const isLightVideo = await FJVideoUtil.isLightBackgroundVideo(fileOrUrl);
        const videoUrl = isExistS3 ? fileDownLoadUrl : FJS3Config.S3_SERVER_HOST + videoUploadKey;
        const thumbnailUrl = FJS3Config.S3_SERVER_HOST + thumbnailUploadKey;
        const __saveToLocal = async () => {
            // 预先加载文件到本地
            await FJUtil.wait(1);
            FJVideoUtil.getFileListFromLocalStorage(videoUrl).then(res => {
                if (!res) {
                    if (isExistS3) {
                        FJUtil.getBlobByUrl(fileDownLoadUrl, true);
                    } else {
                        FJVideoUtil.saveFileListToLocalStorage([{ key: videoUrl, file: videoFile }]);
                    }
                }
            });
        };
        __saveToLocal();
        const params = {
            video_url: videoUrl,
            thumbnail: thumbnailUrl,
            language: textLanguageKey,
            duration_type: clipLengthKey,
            subtitle_id: subtitleStyleId,
            prompt: captureKeyMoments,
            genre: genreKey,
            aspect_ratio: aspectRatioKey,
            duration: Math.floor(duration),
            name: name,
            source_url: sourceUrl,
            resolution_label: resolutionLabel,
            disable_face_data: disableFaceData,
            is_light_video: isLightVideo,
            force_fail: window.isTest,
        };
        if (FJCoreConfig.needUploadAudio) {
            params.audio_url = audioUrl;
        }

        return params;
    },
    async _getSubParams(options = {}) {
        const { videoUrl, start, end, eventId, subId, language, genre = 'auto', subtitle } = options;

        const params = {
            video_url: videoUrl,
            start,
            end,
            event_id: eventId,
            sub_id: subId,
            language,
            clip_goal: genre,
            subtitle: subtitle,
            force_fail: window.isTest,
        };

        return params;
    },
    async _createGeneratingTask(params, taskType = 'ai-long-to-short') {
        const { data, code } = await FJNetworkManager.createGeneratingTask({
            params,
            type: taskType,
        });
        if (code === 401) {
            FJRequirementsUtil.getCredits();
            return;
        } else if (code === 214) {
            FJRequirementsUtil.login();
            return;
        }
        const eventId = data?.event_id;
        return eventId;
    },
    async startSubTask(options = {}) {
        const { callback } = options;
        const params = await this._getSubParams({ ...options });
        try {
            const eventId = await this._createGeneratingTask(params, 'ai-long-to-short-sub');

            if (!eventId) {
                FJLongToShortUpdateStore._notifyNetworkError();
                callback?.({ status: GENERATE_STATE.FAILED, errorCode: JOB_ERROR_CODE.UPLOAD_ERROR });
                return null;
            }
            this._addPollingTasks([eventId]);
            let unsubscribe;
            const _pollingCallback = dataList => {
                const data = dataList.find(data => data.event_id === eventId);
                if (data) {
                    const list = this.parseTaskList([data]);
                    callback?.(list[0]);
                    unsubscribe();
                }
            };
            unsubscribe = this._registerTaskWatchSubscription(null, _pollingCallback);
            !this._isPolling && this._pollingTask();
            return eventId;
        } catch (error) {
            callback?.({ status: GENERATE_STATE.FAILED, errorCode: JOB_ERROR_CODE.UPLOAD_ERROR });
            return;
        }
    },

    async regenerateTask(options = {}) {
        const { eventId, callback, progressCallback } = options;
        const taskId = eventId;
        if (!taskId) {
            FJLongToShortUpdateStore._notifyNetworkError();
            callback?.({ status: GENERATE_STATE.FAILED, errorCode: JOB_ERROR_CODE.UPLOAD_ERROR });
            return;
        }
        try {
            const result = await FJNetworkManager.aiGenerateRetry({ eventId: taskId });
            if (result === undefined) {
                FJLongToShortUpdateStore._notifyNetworkError();
                callback?.({ status: GENERATE_STATE.FAILED, errorCode: JOB_ERROR_CODE.UPLOAD_ERROR });
                return;
            }
        } catch {
            FJLongToShortUpdateStore._notifyNetworkError();
            callback?.({ status: GENERATE_STATE.FAILED, errorCode: JOB_ERROR_CODE.UPLOAD_ERROR });
            return;
        }

        this._addPollingTasks([taskId]);
        const _progressCallback = ({ id, progress }) => {
            if (id === taskId) {
                progressCallback?.({ id, progress });
            }
        };
        let unsubscribe;
        const _pollingCallback = dataList => {
            const data = dataList.find(data => data.event_id === taskId);
            if (data) {
                const list = this.parseTaskList([data]);
                callback?.(list[0]);
                unsubscribe();
            }
        };
        unsubscribe = this._registerTaskWatchSubscription(_progressCallback, _pollingCallback);
        !this._isPolling && this._pollingTask();
    },
    async trackingTask(options = {}) {
        const { eventId, callback, progressCallback } = options;
        this._addPollingTasks([eventId]);
        const _progressCallback = ({ id, progress }) => {
            if (id === eventId) {
                progressCallback?.({ id, progress });
            }
        };
        let unsubscribe;
        const _pollingCallback = dataList => {
            const data = dataList.find(data => data.event_id === eventId);
            if (data) {
                const list = this.parseTaskList([data]);
                callback?.(list[0]);
                unsubscribe();
            }
        };
        unsubscribe = this._registerTaskWatchSubscription(_progressCallback, _pollingCallback);

        !this._isPolling && this._pollingTask();
    },

    _clearPolling() {
        this._isPolling = false;
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }
    },
    startPollingTasks(ids, { callback, progressCallback }) {
        this._addPollingTasks(ids);
        let _ids = ids;
        const _progressCallback = ({ id, progress }) => {
            if (ids.includes(id)) {
                progressCallback?.({ id, progress });
            }
        };
        let unsubscribe;
        const _pollingCallback = dataList => {
            const data = dataList.filter(data => ids.includes(data.event_id));
            if (data.length) {
                const list = this.parseTaskList(data);
                _ids = _ids.filter(id => !list.find(d => d.eventId === id));
                callback?.(list);
                if (!_ids.length) {
                    unsubscribe();
                }
            }
        };

        unsubscribe = this._registerTaskWatchSubscription(_progressCallback, _pollingCallback);
        !this._isPolling && this._pollingTask();
    },

    async _pollingTask() {
        const taskIds = this.getPollingTaskIds();
        if (!taskIds.length) return;

        this._clearPolling();
        this._isPolling = true;

        const scheduleNextPoll = () => {
            this._clearPolling();
            if (!this.getPollingTaskIds().length) return;
            this._isPolling = true;
            this._timer = setTimeout(() => {
                void this._pollingTask();
            }, TASK_POLLING_INTERVAL);
        };

        try {
            const result = await FJNetworkManager.getNewTaskState(taskIds.join(','));
            if (!result) {
                this._errorTime++;
                if (this._errorTime >= MAX_POLLING_ERROR_TIME) {
                    this._clearPolling();
                    const ids = this.getPollingTaskIds();
                    const payloads = buildPollingFailurePayloads(ids);
                    this._taskWatchSubscriptions.forEach(s => s.polling?.(payloads));
                } else {
                    scheduleNextPoll();
                }
                return;
            }

            this._errorTime = 0;

            const rows = normalizePollingResult(result);
            const callbackData = [];
            const { start, proportion } = this.getProcessBase(PROCESS_TYPE.BACKEND);

            for (const item of rows) {
                const state = item.job_state;
                if (state === JOB_STATE.PROCESSING) {
                    if (item.progress) {
                        const p = (Number(item.progress) / 100) * proportion + start;
                        this._taskWatchSubscriptions.forEach(s => s.progress?.({ id: item.event_id, progress: p }));
                    }
                    continue;
                }
                if (POLLING_TERMINAL_JOB_STATES.has(state)) {
                    callbackData.push(item);
                    this._deletePollingTask(item.event_id);
                    FJRequirementsUtil.updateAllCreditInfo();
                }
            }

            this._taskWatchSubscriptions.forEach(s => s.polling?.(callbackData));
            this._clearPolling();
            if (this.getPollingTaskIds().length) {
                this._timer = setTimeout(() => {
                    void this._pollingTask();
                }, TASK_POLLING_INTERVAL);
            }
        } catch (error) {
            console.error(error);
            this._clearPolling();
            return null;
        }
    },
    /**
     * 解析任务列表接口 data：支持数组或 { list/items/data, total? }
     * @param {*} data
     * @returns {{ rows: Record<string, unknown>[], total: number | null }}
     */
    _extractTaskListPayload(data) {
        if (data == null) {
            return { rows: [], total: null };
        }
        if (Array.isArray(data)) {
            return { rows: data, total: null };
        }
        const rowsRaw = data.list ?? data.items ?? (Array.isArray(data.data) ? data.data : null) ?? [];
        const rows = Array.isArray(rowsRaw) ? rowsRaw : [];
        const total =
            typeof data.total === 'number'
                ? data.total
                : typeof data.total_count === 'number'
                  ? data.total_count
                  : typeof data.count === 'number'
                    ? data.count
                    : null;
        return { rows, total: Number.isFinite(total) ? total : null };
    },

    /**
     * 分页拉取任务列表（offset/limit，offset 与接口一致从 1 起算）
     * @param {{ offset?: number, limit?: number }} options
     * @returns {Promise<{ items: any[], nextOffset: number, hasMore: boolean, total: number | null } | null>}
     */
    async getTaskListPage({ offset = 1, limit = FJConfig.TASK_LIST_PAGE_SIZE } = {}) {
        try {
            const data = await FJNetworkManager.getTaskList({ offset, limit });
            if (data == null) {
                return null;
            }
            const { rows, total } = this._extractTaskListPayload(data);
            const items = this.parseTaskList(rows);
            const nextOffset = offset + rows.length;
            let hasMore;
            if (total != null) {
                hasMore = nextOffset < total;
            } else {
                hasMore = rows.length >= limit;
            }
            return { items, nextOffset, hasMore, total };
        } catch (error) {
            console.error(error);
            return null;
        }
    },
    /**
     * 将任务列表接口行转为 `ProjectItem` 形态
     * @param {Record<string, unknown>[]} rows
     */
    parseTaskList(rows) {
        return rows.map(item => this._parseProjectItem(item));
    },

    _parseProjectItem(data) {
        const { out_put = {}, event_id, request_params = {}, created_time, id, error = {} } = data;
        const {
            video_url,
            duration_type,
            source_url,
            thumbnail: thumbnail_url,
            resolution_label,
            duration,
            disable_face_data,
            language,
            genre,
            is_light_video,
            name: fileName,
        } = request_params;
        const { name, thumbnail, ai_extension = [] } = out_put;
        const { progress, status } = this._calcStatusProgress(data);
        const details = this._parseDetails(data);
        return {
            id: id != null ? String(id) : '',
            eventId: event_id,
            videoUrl: video_url,
            thumbnailUrl: thumbnail || thumbnail_url,
            durationType: duration_type,
            title: name,
            timestamp: FJUtil.formatTaskListTimestamp(created_time),
            status,
            progress,
            errorCode: error?.error_msg,
            details,
            duration,
            disableFaceData: disable_face_data,
            language,
            genre,
            stableFragments: this.normalizeStableFragments(ai_extension),
            videoMeta: {
                thumbnailUrl: thumbnail,
                title: fileName,
                sourceUrl: source_url,
                resolutionLabel: resolution_label,
                duration: duration,
                isLightVideo: is_light_video,
                fps: FJUtil.getFpsFromStableFragments(ai_extension),
            },
        };
    },
    _calcStatusProgress(data) {
        const { progress = 0, out_put = {}, job_state: taskJobState } = data;
        let { job_state } = out_put;
        let _p = 25; //默认已经完成上传

        const __fixedNumber = p => {
            return Number((p * 100).toFixed(0) / 100);
        };
        const backendBase = FJLongToShortStore.getProcessBase(PROCESS_TYPE.BACKEND);
        switch (job_state) {
            case JOB_STATE.PROCESSING:
            case JOB_STATE.FAILED:
                _p = backendBase.start * 100 + (progress ?? 0) * backendBase.proportion;
                break;
            case JOB_STATE.SUCCEEDED:
                _p = 100;
                break;
            case JOB_STATE.PENDING:
                _p = backendBase.start * 100 + backendBase.proportion * 100;
                break;
            default:
                break;
        }
        if ([JOB_STATE.TIMEOUT, JOB_STATE.FAILED, JOB_STATE.CANCELED, JOB_STATE.DELETED].includes(taskJobState)) {
            job_state = JOB_STATE.FAILED;
        }
        return {
            progress: __fixedNumber(_p),
            status: job_state,
        };
    },
    _parseDetails(data) {
        const { out_put = {}, request_params = {} } = data;
        const { output = [] } = out_put;
        const { subtitle_id, aspect_ratio } = request_params;
        if (!Array.isArray(output)) {
            return [];
        }
        const details = output.map(item => {
            const {
                id,
                title,
                start,
                end,
                score,
                description,
                subtitle,
                file_key,
                thumbnail,
                liked,
                disliked,
                published_list = [],
                face_data,
                job_state,
                event_id,
            } = item;
            const thumbUrl = thumbnail ?? '';
            const videoData = {
                thumbnailUrl: thumbUrl,
                thumbnailUrlStatus: String(thumbUrl).trim() ? GENERATE_STATE.SUCCEEDED : GENERATE_STATE.PROCESSING,
                startTime: start,
                endTime: end,
                fileKey: file_key,
            };
            const subtitleList = subtitle.map(item => {
                const { text, start_second, end_second, is_translated, transcription_text, transcription_words } = item;
                const transcriptionWords =
                    transcription_words && transcription_words.map(FJSubtitleUtil.normalizeTranscriptionWord);
                return {
                    text,
                    start: start_second,
                    end: end_second,
                    isTranslated: is_translated,
                    transcriptionText: transcription_text,
                    transcriptionWords,
                };
            });
            const publishedList = FJUtil.parseString(published_list);
            return {
                id: id.toString(),
                title,
                subEventId: event_id,
                status: job_state,
                score,
                summary: description,
                start,
                end,
                videoData,
                subtitleList,
                subtitleStyleId: subtitle_id,
                publishedList,
                liked,
                disliked,
                exportRatio: aspect_ratio,
                faceData: face_data,
            };
        });
        return details;
    },
    /**
     * @param {{ start_time?: unknown, end_time?: unknown }[]} raw
     * @returns {{ start_time: number, end_time: number }[]}
     */
    normalizeStableFragments(raw) {
        try {
            const TIME_SLICE_EPS = 1e-9;
            if (!Array.isArray(raw) || raw.length === 0) {
                return [];
            }
            /** @type {{ start_time: number, end_time: number }[]} */
            const out = [];
            const fps = FJUtil.getFpsFromStableFragments(raw);
            const frameTime = 1 / fps;
            const timeDiff = 0.3;
            for (const f of raw) {
                const sft = Number(f?.start_frame) * frameTime;
                const eft = Number(f?.end_frame) * frameTime;
                const st = Number(f?.start_time);
                const en = Number(f?.end_time);
                const isOutDiff = Math.abs(sft - st) > timeDiff || Math.abs(eft - en) > timeDiff;
                if (Number.isFinite(sft) && Number.isFinite(eft) && eft > sft + TIME_SLICE_EPS && !isOutDiff) {
                    out.push({ startTime: sft, endTime: eft });
                    continue;
                }

                if (Number.isFinite(st) && Number.isFinite(en) && en > st + TIME_SLICE_EPS) {
                    out.push({ startTime: st, endTime: en });
                }
            }
            out.sort((a, b) => a.startTime - b.startTime);
            return out;
        } catch (error) {
            console.error(error);
            return [];
        }
    },
    _parseSubtitleItem(item) {
        const { text, start_second, end_second, is_translated, transcription_text, transcription_words } = item;
        return {
            text,
            start: start_second,
            end: end_second,
            isTranslated: is_translated,
            transcriptionText: transcription_text,
            transcriptionWords:
                transcription_words && transcription_words.map(FJSubtitleUtil.normalizeTranscriptionWord),
        };
    },

    /**
     * 获取某处理阶段在总进度条上的起点与占比。
     * @param {string} type - PROCESS_TYPE 之一
     * @param {object} [options]
     * @param {boolean} [options.disableFaceData]
     * @param {boolean} [options.needExport]
     * @param {boolean} [options.needUploadAudio]
     * @param {object} [options.weights] 与 DEFAULT_PROCESS_PROGRESS_WEIGHTS 同名字段的局部覆盖
     */
    getProcessBase(type, options = {}) {
        const { weights: weightOverrides, ...segmentOptions } = options;
        const weights = mergeProcessProgressWeights(weightOverrides);
        const seg = computeProcessProgressSegments(segmentOptions, weights);
        switch (type) {
            case PROCESS_TYPE.UPLOAD_VIDEO:
                return { start: seg.uploadVideoS, proportion: seg.uploadVideoP };
            case PROCESS_TYPE.UPLOAD_AUDIO:
                return { start: seg.uploadAudioS, proportion: seg.uploadAudioP };
            case PROCESS_TYPE.BACKEND:
                return { start: seg.backendS, proportion: seg.backendP };
            case PROCESS_TYPE.FACE_DETECT:
                return { start: seg.facetS, proportion: seg.faceP };
            case PROCESS_TYPE.EXPORT:
                return { start: seg.exportS, proportion: seg.exportP };
            case PROCESS_TYPE.COVER_THUMBNAILS:
                return { start: seg.coverThumbnailsS, proportion: seg.coverThumbnailsP };
            default:
                return { start: 0, proportion: 0 };
        }
    },
    isHighlightExtractionFailure(errorCode) {
        return errorCode === JOB_ERROR_CODE.NO_TRANSCRIPTION || errorCode === JOB_ERROR_CODE.NO_HIGHLIGHTS_DETECTED;
    },
};

export default FJLongToShortStore;
